/**
 * What an address that leads nowhere says, and what a screen that throws says.
 *
 * Both exist because React Router's own answer is a developer's page — a bare "Unexpected
 * Application Error!" on a white background, with a stack trace where the way out should be. That
 * is the wrong thing to hand someone at a registration desk, and the two ways to reach it are
 * ordinary: a bookmark from before a route was renamed, and a screen that fails to load because the
 * network dropped between two chunks.
 *
 * Neither screen apologises at length. It says what happened, and then it offers the two doors that
 * are always right — back to where you were, or back to the start.
 */

import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";
import { Button, Card, EmptyState } from "@powwow/ui";

export function NotFoundScreen() {
  const navigate = useNavigate();

  return (
    <EmptyState
      title="That address does not exist"
      description="The link may be out of date, or the page may have moved. No work has been lost; anything you were working on remains where you left it."
      action={
        <div className="flex flex-wrap justify-center gap-3">
          <Button onClick={() => navigate(-1)}>Go back</Button>
          <Button variant="quiet" onClick={() => navigate("/dashboard")}>
            Go to the dashboard
          </Button>
        </div>
      }
    />
  );
}

/**
 * A screen whose data would not load.
 *
 * The old answer was a bare red banner floating on an otherwise empty page: no heading, no name for
 * the screen you were on, and no way onward. Following a link to something your role does not open
 * — an email pointing a registrar at the members list, a bookmark a treasurer kept — is ordinary,
 * and it produced the least helpful page in the application.
 *
 * A 403 is separated out because it is not a fault. Nothing is broken and retrying will not help;
 * the useful sentence is which screen this is, that the permission is the reason, and where to go
 * instead. Everything else keeps *Try again*, because everything else might work on the second go.
 */
export function ScreenError({
  screen,
  message,
  status,
  onRetry,
}: {
  /** The screen's own title, so the page still says what it is. */
  screen: string;
  message: string;
  status?: number | null | undefined;
  onRetry?: (() => void) | undefined;
}) {
  const navigate = useNavigate();
  const denied = status === 403;

  return (
    <>
      <h1 className="mb-4 text-heading font-bold">{screen}</h1>
      <EmptyState
        title={denied ? "You do not have access to this screen" : "This screen did not load"}
        description={
          denied
            ? `Your role does not include ${screen.toLowerCase()}. Nothing is broken; a member with permission to manage responsibilities can update your access from the members list.`
            : message
        }
        action={
          <div className="flex flex-wrap justify-center gap-3">
            {!denied && onRetry && <Button onClick={onRetry}>Try again</Button>}
            <Button variant={denied ? "primary" : "quiet"} onClick={() => navigate("/dashboard")}>
              Go to Today
            </Button>
          </div>
        }
      />
    </>
  );
}

/**
 * The router's error boundary.
 *
 * A 404 thrown by the router is the same situation as an unknown address, so it renders the same
 * screen. Anything else is a genuine fault, and the one thing that must not happen is a person
 * concluding their work was lost — a failed *render* has never discarded a queued write, and saying
 * so is more useful than the error text.
 */
export function RouteErrorScreen() {
  const error = useRouteError();
  const navigate = useNavigate();

  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundScreen />;

  // Kept for the person who is going to be asked "what did it say?" — visible, but not the headline.
  const detail =
    error instanceof Error ? error.message : isRouteErrorResponse(error) ? error.statusText : null;

  return (
    <EmptyState
      title="This screen did not load"
      description={
        <>
          <span>
            Something went wrong opening this page. Anything already saved remains safe, and
            anything waiting to send is still queued; this stopped a screen, not your work.
          </span>
          {detail && (
            <Card className="mt-4 text-left text-caption text-muted" aria-label="Technical detail">
              {detail}
            </Card>
          )}
        </>
      }
      action={
        <div className="flex flex-wrap justify-center gap-3">
          <Button onClick={() => window.location.reload()}>Try again</Button>
          <Button variant="quiet" onClick={() => navigate("/dashboard")}>
            Go to the dashboard
          </Button>
        </div>
      }
    />
  );
}
